import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { sanitizeQueryParam } from '@v2/lib';
import '@v2/styles/tailwind.css';
import { PartnersTable, PartnerAccountPanel, PartnerOfferingPanel, PartnerDomainPanel } from './components';
import { Partner, PartnerOfferingWithBitwerx } from '@v2/types';
import { useBitwerxOfferings, useDeleteDomain, usePartnerDetails, usePartners, useTableFilters } from '@v2/hooks';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { parsePartnerOfferings } from './utils';
import { CreateDomainForm, CreatePartnerForm, ManagePartnerAccounts, OfferingForm } from '@v2/features';

const PartnersPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false);
  const [isCreatePartnerModalOpen, setIsCreatePartnerModalOpen] = useState(false);
  const [isManagePartnerAccountsModalOpen, setIsManagePartnerAccountsModalOpen] = useState(false);
  const [isManagePartnerOfferingModalOpen, setIsManagePartnerOfferingModalOpen] = useState(false);
  const [selectedPartnerOffering, setSelectedPartnerOffering] = useState<PartnerOfferingWithBitwerx | null>(null);
  const searchParams = new URLSearchParams(location.search);
  const selectedPartnerId = sanitizeQueryParam(searchParams.get('partnerId'));

  const {
    partners,
    loading: partnersLoading,
    error: partnersError,
    refetch: refetchPartners,
  } = usePartners();

  const {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
  } = useTableFilters({
    data: partners,
    searchKeys: ['name'],
  });

  const {
    partner,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = usePartnerDetails(selectedPartnerId);

  const {
    offerings: bitwerxOfferings,
  } = useBitwerxOfferings();

  const { deleteDomain } = useDeleteDomain();

  const handleSelectPartner = (partner: Partner) => {
    searchParams.set('partnerId', partner.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('partnerId');
    history.push({ search: searchParams.toString() });
  };

  const handleCreatePartner = () => {
    setIsCreatePartnerModalOpen(true);
  };

  const onCompletedCreatePartner = () => {
    setIsCreatePartnerModalOpen(false);
    refetchPartners();
  };

  const handleDeleteDomain = async (domainId: string) => {
    const success = await deleteDomain(domainId);
    if (success) {
      toast.success('Domain deleted successfully');
      refetchDetails();
    } else {
      toast.error('Failed to delete domain');
    }
  };

  const onCompletedAddDomain = () => {
    setIsAddDomainModalOpen(false);
    refetchDetails();
  };

  const onCompletedAddAccount = () => {
    refetchDetails();
  };

  return (
    <>
    <SettingsPageLayout
      icon={faUsers}
      title="Partners"
      actionLabel="Create Partner"
      onAction={handleCreatePartner}
    >
      <ListDetailView
        selectedId={selectedPartnerId}
        list={
          <PartnersTable
            partners={filteredData}
            loading={partnersLoading}
            error={partnersError}
            onSelectPartner={handleSelectPartner}
            selectedPartnerId={selectedPartnerId || undefined}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
          />
        }
        detail={
          <DetailPanel
            title={partner?.name || 'Partner Details'}
            subtitle={partner?.id ? `(${partner.id})` : undefined}
            onClose={handleClosePanel}
            loading={detailsLoading}
            loadingMessage="Loading partner details..."
            error={detailsError}
            errorMessage="There was an error fetching the partner details."
            onRetry={refetchPartners}
          >
            {partner && (
                  <div className="tw-flex tw-flex-col tw-gap-4">
                    <PartnerAccountPanel
                      accounts={partner.accounts}
                      partnerId={partner.id}
                      handleAddAccount={() => setIsManagePartnerAccountsModalOpen(true)}
                      onAccountRemoved={refetchDetails}
                    />
                    <PartnerOfferingPanel
                      offerings={parsePartnerOfferings(partner.partnerOfferings, bitwerxOfferings)}
                      partnerId={partner.id}
                      onEditOffering={(offering) => {
                        setSelectedPartnerOffering(offering);
                        setIsManagePartnerOfferingModalOpen(true);
                      }}
                      onAddOffering={() => {
                        setSelectedPartnerOffering(null);
                        setIsManagePartnerOfferingModalOpen(true);
                      }}
                    />
                    <PartnerDomainPanel
                      domains={partner.trustedDomains}
                      handleAddDomain={() => setIsAddDomainModalOpen(true)}
                      handleDeleteDomain={handleDeleteDomain}
                    />
                </div>
            )}
          </DetailPanel>
        }
      />
    </SettingsPageLayout>
    {isAddDomainModalOpen && (
      <CreateDomainForm
        onCancel={() => setIsAddDomainModalOpen(false)}
        onCompleted={onCompletedAddDomain}
      />
    )}
    {isCreatePartnerModalOpen && (
      <CreatePartnerForm
        onCancel={() => setIsCreatePartnerModalOpen(false)}
        onCompleted={onCompletedCreatePartner}
      />
    )}
    {isManagePartnerAccountsModalOpen && (
      <ManagePartnerAccounts
        onClose={() => setIsManagePartnerAccountsModalOpen(false)}
        onCompleted={onCompletedAddAccount}
      />
    )}
    {isManagePartnerOfferingModalOpen && selectedPartnerId && (
      <OfferingForm
        bitwerxOfferings={bitwerxOfferings}
        partnerId={selectedPartnerId}
        partnerOffering={selectedPartnerOffering}
        onClose={() => {
          setIsManagePartnerOfferingModalOpen(false);
          setSelectedPartnerOffering(null);
        }}
        onCompleted={() => {
          setIsManagePartnerOfferingModalOpen(false);
          setSelectedPartnerOffering(null);
          refetchDetails();
        }}
      />
    )}
    </>
  );
};

export default PartnersPage;
